/**
 * useProfile – shared hook that fetches /api/users/me exactly once per session.
 *
 * A module-level cache means every component that calls useProfile() shares
 * the same in-flight request and, once resolved, receives the result
 * synchronously without a second network trip.
 *
 * Call clearProfileCache() on logout so the next login triggers a fresh fetch.
 */
import { useState, useEffect } from 'react';
import { apiFetch } from '../context/AuthContext';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// module-level cache – survives re-renders, cleared on logout
let _cachedProfile = null;
let _inflight = null;           // shared Promise while fetch is in flight

function fetchProfile() {
  if (_inflight) return _inflight;
  _inflight = apiFetch(`${BASE_URL}/users/me`, { method: 'GET' })
    .then((r) => r.json())
    .then((data) => {
      if (data?.success && data?.profile) {
        _cachedProfile = data.profile;
        return data.profile;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => { _inflight = null; });
  return _inflight;
}

export function useProfile() {
  const [profile, setProfile] = useState(_cachedProfile);
  const [profileLoading, setProfileLoading] = useState(!_cachedProfile);

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      if (_cachedProfile) {
        setProfile(_cachedProfile);
        setProfileLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setProfile(null);
        setProfileLoading(false);
        return;
      }

      setProfileLoading(true);
      fetchProfile().then((p) => {
        if (cancelled) return;
        setProfile(p);
        setProfileLoading(false);
      });
    };

    load();

    // The mount-only fetch above is enough for components that remount on
    // every visit (most pages). It's NOT enough for a component that stays
    // mounted across an entire SPA session (the notification socket, the
    // Mini Chat Dock) — logging out and into a different account without a
    // page reload would leave `profile` stuck on the previous user forever,
    // since nothing else re-runs this effect. Re-sync on the same
    // login/logout signals AuthContext already dispatches.
    const onLogout = () => {
      setProfile(null);
      setProfileLoading(false);
    };
    const onLogin = () => load();
    window.addEventListener('hrms:auth:logout', onLogout);
    window.addEventListener('hrms:auth:login', onLogin);

    return () => {
      cancelled = true;
      window.removeEventListener('hrms:auth:logout', onLogout);
      window.removeEventListener('hrms:auth:login', onLogin);
    };
  }, []);

  return { profile, profileLoading };
}

/** Call this on logout to force a fresh fetch on the next login. */
export function clearProfileCache() {
  _cachedProfile = null;
  _inflight = null;
}
